import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import React, { useCallback, useMemo, useState } from "react"
import { Link } from "../../../../server/router"
import { Table } from "../../../uiKit/Table/Table"
import { TableColumn } from "../../../uiKit/Table/types"
import { MDash } from "@figurepos/fgr-lib-business-logic/lib/constants/symbols"
import { AntdIconWrap } from "../../../uiKit/Icons/AntdIconWrap"
import {
    MultiSelect,
    MultiSelectItem,
    MultiSelectSearchMode,
    MultiSelectSelectMode,
} from "../../../uiKit/MultiSelect/MultiSelect"
import { Button } from "antd"
import { getItemsAndCategoriesListResponse } from "../selectors"
import { useSelector } from "react-redux"
import { type AddedToItem } from "../../../../graphql/types"
import { getRootCategory } from "../itemsCategories/table/selectors"
import { getItemsAndCategoriesMap, getItemsAndCategoriesMultiSelectData } from "./utils"

export type AddItemsToGroupTableItem = {
    id: string
    name: string
    categoryName: Maybe<string>
}

type Props = {
    addedToItems: AddedToItem[]
    onItemsChanged: (newItems: AddedToItem[]) => void
}

export const AddItemsToGroupTable = ({ addedToItems, onItemsChanged }: Props) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const itemsAndCategories = useSelector(getItemsAndCategoriesListResponse)?.data
    const rootCategoryId = useSelector(getRootCategory)?.id

    const itemsMap = useMemo(() => getItemsAndCategoriesMap(itemsAndCategories), [itemsAndCategories])
    const pickerData: MultiSelectItem[] = useMemo(
        () =>
            getItemsAndCategoriesMultiSelectData(
                itemsAndCategories,
                addedToItems.map(i => i.id),
                rootCategoryId
            ),
        [itemsAndCategories, addedToItems, rootCategoryId]
    )

    const tableData: AddItemsToGroupTableItem[] = useMemo(() => {
        return addedToItems
            .map(addedItem => {
                const item = itemsMap[addedItem.id]
                if (!item) {
                    return null
                }
                return {
                    id: item.id,
                    name: item.name,
                    categoryName: item.parentId && itemsMap[item.parentId] ? itemsMap[item.parentId].name : null,
                }
            })
            .filter(Boolean) as AddItemsToGroupTableItem[]
    }, [addedToItems, itemsMap])

    const onNewItemsAdded = (selectedIds: string[]) => {
        const existingIds = addedToItems.map(i => i.id)
        const newItems: AddedToItem[] = selectedIds
            .filter(id => !existingIds.includes(id))
            .map(id => {
                const item = itemsMap[id]
                if (!item) {
                    return null
                }
                return { id: id, name: item.name }
            })
            .filter(Boolean) as AddedToItem[]

        onItemsChanged([...addedToItems, ...newItems])

        setSelectedIds([])
    }

    const onItemRemoved = useCallback(
        (id: string) => {
            onItemsChanged(addedToItems.filter(i => i.id !== id))
        },
        [addedToItems, onItemsChanged]
    )

    const columns: TableColumn<AddItemsToGroupTableItem>[] = useMemo(
        () => [
            {
                id: "name",
                getData: rowData => rowData.name,
                header: () => <span className={classes.headerBold}>Added to Items</span>,
                cell: rowData => <Link href={`/menu/categories/edit/${rowData.id}`}>{rowData.name}</Link>,
            },
            {
                id: "categoryName",
                getData: rowData => rowData.categoryName,
                header: () => <span className={classes.headerBold}>Category</span>,
                cell: rowData => rowData.categoryName || <span className={classes.muted}>{MDash}</span>,
            },
            {
                id: "actions",
                cell: rowData => (
                    <button type="button" className={classes.removeIconWrap} onClick={() => onItemRemoved(rowData.id)}>
                        <AntdIconWrap className={classes.removeIcon} type="close" />
                    </button>
                ),
                align: "right",
                width: 40,
            },
        ],
        [onItemRemoved]
    )

    return (
        <div>
            <Table
                columns={columns}
                data={tableData}
                noDataLabel="This Modifier Group is not added to any Items"
                bodyRowClassName={classes.bodyRow}
                headerRowClassName={classes.headerRow}
                tableWrapClassName={classes.roundedBottomNone}
                wrapClassName={classes.roundedBottomNone}
            />
            <div className={classes.addItemsWrap}>
                <MultiSelect
                    collapseNested={true}
                    data={pickerData}
                    onChange={onNewItemsAdded}
                    value={selectedIds}
                    selectMode={MultiSelectSelectMode.ItemsOnly}
                    searchMode={MultiSelectSearchMode.KeepChildren}
                    keepSelectedOnSearch={true}
                    searchPlaceholder="Search for category or item…"
                    dropDownClassName={classes.dropdown}
                    renderActionButtons={(onApply, onCancel, selectedIds) => (
                        <div className={classes.actionButtonsWrap}>
                            <Button className={classes.actionButton} onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                className={classes.actionButton}
                                type="primary"
                                onClick={onApply}
                                disabled={!selectedIds.length}>
                                Apply to Items
                            </Button>
                        </div>
                    )}
                    renderTriggerElement={buttonProps => (
                        <button {...buttonProps} type="button" className={classes.addItemsButtonWrap}>
                            <AntdIconWrap type="plus-circle" theme="filled" className={classes.addItemsIcon} />
                            Add to Items
                        </button>
                    )}
                />
            </div>
        </div>
    )
}

const classes = {
    headerBold: "font-bold",
    removeIconWrap: "bg-transparent cursor-pointer mr-[6px]",
    removeIcon: "text-error text-[16px]",
    muted: "opacity-50",
    bodyRow: "[&>td:first-child]:pl-4 [&>td:last-child]:pr-4",
    headerRow: "[&>th:first-child]:pl-4 [&>th:last-child]:pr-4",
    roundedBottomNone: "rounded-b-none",
    addItemsWrap: "rounded-[4px] rounded-t-none border-gray-200 border -mt-px",
    dropdown: "max-w-[622px] max-h-[400px]",
    actionButtonsWrap: "flex space-x-2 px-2 pt-2",
    actionButton: "flex-1",
    addItemsButtonWrap:
        "block bg-transparent w-full text-left text-primary-color cursor-pointer px-6 py-4 hover:bg-primary-color/5 transition-colors duration-200",
    addItemsIcon: "mr-2 text-[16px]",
}
